import { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Dashboard } from '@pages/Dashboard';
import { TransferPage } from '@pages/TransferPage';
import { LoginPage } from '@pages/LoginPage';
import { AuthCallback } from '@pages/AuthCallback';
import { PlayerNotConnected } from '@pages/PlayerNotConnected';
import { useStore } from '@services/store';
import { authService } from '@services/auth.service';
import { minecraftApiService } from '@services/minecraft-api.service';
import { decodeJWT } from '@utils/jwt';
import type { PlayerTokenPayload } from '@/types/crisp';
import type { PlayerData } from '@/types/index';

function App() {
  const { setCurrentPlayer, setLoadingPlayer, setSession } = useStore();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [playerNotConnected, setPlayerNotConnected] = useState(false);
  const loaderRef = useRef<HTMLDivElement>(null);

  // Vérifier si on est sur la page de callback
  const isCallbackPage = window.location.pathname === '/microsoft/crisp-widget-redirect';

  // Ajuster la hauteur du loader
  useEffect(() => {
    if (isAuthenticated === null && loaderRef.current) {
      const timer = setTimeout(() => {
        if (loaderRef.current && (window as any).$crisp?.setHeight) {
          const height = loaderRef.current.scrollHeight;
          (window as any).$crisp.setHeight(Math.min(height, 300));
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Skip auth check si on est sur la page de callback
    if (isCallbackPage) {
      return;
    }

    const initializeWidget = async () => {
      console.log('=== WIDGET INITIALIZATION ===');
      try {
        // 1. Vérifier si l'opérateur a un JWT valide
        const hasToken = authService.isAuthenticated();
        console.log('🔍 Step 1: Check if token exists in localStorage:', hasToken);

        if (!hasToken) {
          console.log('🔴 No valid JWT found, showing login page');
          setIsAuthenticated(false);
          return;
        }

        console.log('✅ JWT found in localStorage, verifying with backend...');

        // 2. Vérifier le JWT auprès du backend (vérifie que l'UUID est autorisé)
        const isValid = await authService.verifyToken();
        console.log('🔍 Step 2: Backend validation result:', isValid);

        if (!isValid) {
          console.log('🔴 JWT validation failed, operator not authorized');
          authService.logout();
          setIsAuthenticated(false);
          return;
        }

        console.log('✅ JWT validated successfully');

        // 3. Récupérer les infos de l'opérateur depuis le JWT
        const operatorInfo = authService.getOperatorInfo();
        if (!operatorInfo) {
          console.error('❌ Failed to decode operator info from JWT');
          authService.logout();
          setIsAuthenticated(false);
          return;
        }

        console.log('👤 Operator:', operatorInfo.username, '(UUID:', operatorInfo.uuid, ')');

        // 4. Récupérer les données du joueur depuis Crisp
        setLoadingPlayer(true);
        setIsAuthenticated(true);

        // Récupérer le player_token depuis Crisp session data
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const websiteId = urlParams.get('website_id');

        console.log('=== CRISP SESSION ===');
        console.log('Session ID:', sessionId);
        console.log('Website ID:', websiteId);

        if (!sessionId || !websiteId) {
          console.warn('⚠️ Missing session_id or website_id in URL');
          setLoadingPlayer(false);
          return;
        }

        // Stocker les données de session dans le store
        const operatorToken = authService.getToken();
        if (operatorToken) {
          setSession(
            {
              sessionId,
              websiteId,
              operatorId: operatorInfo.uuid,
              operatorName: operatorInfo.username,
            },
            operatorToken
          );
          console.log('✅ Session data stored in Zustand store');
        }

        // Appeler l'API worker pour récupérer le java_token
        const response = await fetch('/api/session-data', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`,
          },
          body: JSON.stringify({
            session_id: sessionId,
            website_id: websiteId,
          }),
        });

        const data = await response.json() as { success: boolean; data?: { player_token: string }; error?: string };

        // Si l'erreur indique que le joueur n'est pas connecté
        if (!response.ok || !data.success) {
          const errorMessage = data.error || 'Unknown error';
          console.log('⚠️ API error:', errorMessage);

          // Vérifier si c'est une erreur de joueur non connecté
          if (errorMessage.includes('java_uuid') || errorMessage.includes('java_verifier') || errorMessage.includes('not found')) {
            console.log('⚠️ Player not connected - showing connection request page');
            setLoadingPlayer(false);
            setPlayerNotConnected(true);
            return;
          }

          // Autre erreur
          throw new Error(errorMessage);
        }

        const playerToken = data.data?.player_token || null;
        console.log('player_token retrieved:', playerToken ? '✅' : '❌');

        if (!playerToken) {
          console.log('⚠️ Player not connected - showing connection request page');
          setLoadingPlayer(false);
          setPlayerNotConnected(true);
          return;
        }

        // Décoder le token pour extraire les informations du joueur
        const tokenPayload = decodeJWT<PlayerTokenPayload>(playerToken);

        if (!tokenPayload || !tokenPayload.uuid || !tokenPayload.username) {
          console.error('Invalid player token payload');
          setLoadingPlayer(false);
          return;
        }

        // Récupérer les données Minecraft via le backend
        console.log('Fetching Minecraft data for UUID:', tokenPayload.uuid);
        const minecraftData = await minecraftApiService.getPlayerData(tokenPayload.uuid);
        console.log('Minecraft data resolved:', minecraftData);

        // Créer les données du joueur
        const player: PlayerData = {
          uuid: tokenPayload.uuid,
          username: minecraftData.username,
          avatarUrl: minecraftData.avatarUrl,
          balance: {
            coins: Math.floor(Math.random() * 100000) + 10000,
            tokens: Math.floor(Math.random() * 500) + 50,
          },
          rank: 'VIP',
          lastSeen: new Date().toISOString(),
          playtime: Math.floor(Math.random() * 500) + 100,
          isBanned: false,
          banReason: null,
          banExpiresAt: null,
        };

        setCurrentPlayer(player);

        console.log('✅ Widget initialized successfully');
      } catch (error) {
        console.error('❌ Failed to initialize widget:', error);
        console.error('Error details:', error);
        setIsAuthenticated(false);
      } finally {
        setLoadingPlayer(false);
      }
    };

    console.log('🚀 Calling initializeWidget()...');
    initializeWidget();
  }, [setCurrentPlayer, setLoadingPlayer, isCallbackPage, setSession]);

  // Si on est sur la page de callback, afficher directement le router
  if (isCallbackPage) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/microsoft/crisp-widget-redirect" element={<AuthCallback />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Afficher la page de login si pas authentifié
  if (isAuthenticated === false) {
    return <LoginPage />;
  }

  // Afficher un loader pendant la vérification
  if (isAuthenticated === null) {
    return (
      <div ref={loaderRef} className="w-full bg-white py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  // Afficher la page "Joueur non connecté" si l'opérateur est connecté mais pas le joueur
  if (playerNotConnected) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PlayerNotConnected />} />
          <Route path="/transfer" element={<TransferPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={
          <div className="w-full bg-white">
            <Dashboard />
          </div>
        } />
        <Route path="/transfer" element={<TransferPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
